/**
 * Annotation engine (module singleton): local database, device id + HLC, the device-wide outbox,
 * the per-sheet stores and the sync loop.
 *
 * Generations: everything that belongs to one signed-in user on this device (Dexie connection,
 * write chain, outbox, open stores) lives in a Generation. Logout / user switch deletes the
 * 'kgr-annotations' database (offline/wipe.ts) → 'versionchange' → the generation is killed
 * before anything else can be written, and hooks pick up a fresh one.
 *
 * Sync loop: single-flight; requests that arrive while a cycle runs are merged into the next one.
 * A cycle = pick up ops other tabs stored → flush the outbox (current user's ops only, FIFO,
 * sequential) → pull the requested open sheets. Network failure / 5xx / 429 → stop and retry with
 * backoff; 401 → stop (queue kept).
 *
 * Local write failures (IndexedDB full, WebKit dropping the connection after a suspend): every edit
 * updates memory and the in-memory outbox first, then writes IndexedDB in the generation's chain.
 * When such a write fails, the generation is flagged (writeFailed): open sheets show sync status
 * 'error' while unsent edits exist (they are NOT safe on the device — closing the app offline would
 * lose them), a repair run re-writes the authoritative memory state (outbox + open sheets) with
 * backoff until it succeeds, and the failure is reported once if a repair fails too.
 */
import type { PushAnnotationsResponse } from '../../../shared/dto/annotations';
import type { MeDTO } from '../../../shared/dto/auth';
import type { SongFileDTO } from '../../../shared/dto/library';
import { uuid } from '../../../shared/ids';
import { ApiFailure, serverNow } from '../../api/client';
import { queryClient } from '../../api/queryClient';
import Dexie from 'dexie';
import { ANNOTATION_DB_NAME, AnnotationDb, applyDataVersion, META } from './db';
import { HlcClock } from './hlc';
import { Outbox, annKey, backoffMs, nextStep, type OpRecord } from './queue';
import { SheetStore } from './sheetStore';
import { asLayerDTO, classifyError, failureOf, transport, type NetOutcome } from './transport';
import { isLostIdbConnection } from '../../lib/idb';

type AnnOp = Extract<OpRecord, { type: 'ann' }>;

export const EDIT_FLUSH_DELAY_MS = 1500;
export const SYNC_INTERVAL_MS = 60_000;
/** Delays before re-writing memory to IndexedDB after a failed local write (last one repeats). */
export const REPAIR_DELAYS_MS = [2_000, 10_000, 30_000, 60_000] as const;

/** The signed-in user's query (same key as auth/AuthProvider.tsx, which this .ts module can't import). */
const ME_KEY = ['me'] as const;

const isDev = (import.meta as { env?: { DEV?: boolean } }).env?.DEV === true;

const log = (msg: string, e?: unknown) => {
  if (isDev) console.warn(`[annotations] ${msg}`, e ?? '');
};

/** Sends a local write failure to Stjórnun → Villuskráning (POST /api/client-errors, see errorReporter.ts). */
function reportLocalWriteFailure(e: unknown): void {
  if (typeof window === 'undefined') return;
  void import('../../errorReporter')
    .then((m) => m.reportClientError(e instanceof Error ? e : new Error(String(e)), { kind: 'annotations.local_write' }))
    .catch(() => undefined);
}

export class Generation {
  dead = false;
  db: AnnotationDb | null = null;
  deviceId = '';
  /** Runs on estimated server time (see hlc.ts), so a device with a slow clock still orders its edits right. */
  clock = new HlcClock(0, serverNow);
  outbox = new Outbox();
  /**
   * A transaction of run() failed: memory (and the in-memory outbox) are ahead of IndexedDB until
   * a repair run (AnnotationEngine.repairLocal) has written them again.
   */
  writeFailed = false;
  readonly ready: Promise<void>;
  private chain: Promise<unknown>;

  constructor(
    readonly userId: string,
    private readonly onVersionChange: (g: Generation) => void,
    private readonly onWriteError: (g: Generation, e: unknown) => void = () => undefined,
  ) {
    this.ready = this.init();
    this.chain = this.ready;
  }

  private async init(): Promise<void> {
    let db: AnnotationDb | null = null;
    try {
      if (typeof indexedDB === 'undefined') throw new Error('no IndexedDB');
      db = new AnnotationDb();
      db.on('versionchange', () => this.onVersionChange(this));
      await db.open();
      const d = db;
      let [owner, device, last] = await d.meta.bulkGet([META.userId, META.deviceId, META.lastHlc]);
      if (owner && owner.v !== this.userId) {
        // Data of another user on this device (the logout wipe didn't run): never show or send it.
        await d.transaction('rw', d.tables, () => Promise.all(d.tables.map((t) => t.clear())));
        device = undefined;
        last = undefined;
      }
      this.deviceId = typeof device?.v === 'string' && device.v ? device.v : uuid();
      this.clock = new HlcClock(typeof last?.v === 'number' ? last.v : 0, serverNow);
      const { outbox, rewrite } = Outbox.restore(await d.outbox.toArray());
      this.outbox = outbox;
      await d.transaction('rw', d.meta, d.outbox, d.cursors, async () => {
        await d.meta.bulkPut([
          { k: META.userId, v: this.userId },
          { k: META.deviceId, v: this.deviceId },
        ]);
        if (rewrite.length) await d.outbox.bulkPut(rewrite); // keyed by key: replaces the duplicates
        // Before `ready` resolves, so no sheet store has read (and cached) a cursor yet.
        await applyDataVersion(d);
      });
      if (this.dead) {
        d.close({ disableAutoOpen: true });
        return;
      }
      this.db = d;
    } catch (e) {
      log('local database unavailable; annotations are kept in memory only', e);
      try {
        db?.close({ disableAutoOpen: true });
      } catch {
        /* ignore */
      }
      this.db = null;
      if (!this.deviceId) this.deviceId = uuid();
    }
  }

  /**
   * Runs `fn` in one read-write transaction over all tables, strictly after every earlier run
   * (keeps memory and disk in the same order). Never rejects; resolves undefined on failure or
   * when the generation is gone. A failure flags the generation (writeFailed) and tells the engine.
   * A connection Safari dropped while the app was suspended (lib/idb.ts) is reopened and the
   * transaction run once more first: nothing of the failed one was written.
   */
  run<T>(fn: (db: AnnotationDb) => Promise<T>): Promise<T | undefined> {
    const p = this.chain.then(async () => {
      const db = this.db;
      if (this.dead || !db) return undefined;
      try {
        return await db.transaction('rw', db.tables, () => fn(db));
      } catch (e) {
        if (!isLostIdbConnection(e) || this.dead) throw e;
        const fresh = await this.reopen();
        if (!fresh) return undefined;
        return fresh.transaction('rw', fresh.tables, () => fn(fresh));
      }
    });
    const safe = p.catch((e: unknown) => {
      log('local write failed', e);
      if (!this.dead) {
        this.writeFailed = true;
        try {
          this.onWriteError(this, e);
        } catch {
          /* never let the handler break the chain */
        }
      }
      return undefined;
    });
    this.chain = safe;
    return safe;
  }

  /** A new connection in place of a lost one (null when the generation ended meanwhile). */
  private async reopen(): Promise<AnnotationDb | null> {
    try {
      this.db?.close({ disableAutoOpen: true });
    } catch {
      /* already closed */
    }
    const db = new AnnotationDb();
    db.on('versionchange', () => this.onVersionChange(this));
    await db.open();
    if (this.dead) {
      db.close({ disableAutoOpen: true });
      return null;
    }
    this.db = db;
    return db;
  }

  kill(): void {
    this.dead = true;
    try {
      this.db?.close({ disableAutoOpen: true });
    } catch {
      /* ignore */
    }
    this.db = null;
  }
}

interface SyncRequest {
  pullSheets: Set<string>;
  pullAll: boolean;
  retryFailed: boolean;
}

export interface ScheduleOptions {
  /** Pull these sheets (if open) after the flush. */
  sheets?: Iterable<string>;
  /** Pull every open sheet after the flush. */
  pullAll?: boolean;
  /** Make failed (rejected) ops eligible again. */
  retryFailed?: boolean;
}

export type SyncPhase = 'idle' | 'offline' | 'server' | 'auth';

export class AnnotationEngine {
  private gen: Generation | null = null;
  private genId = 0;
  private genListeners = new Set<() => void>();
  private stores = new Map<string, SheetStore>();

  private loop: Promise<void> | null = null;
  private req: SyncRequest | null = null;
  running = false;
  phase: SyncPhase = 'idle';
  private failures = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private editTimer: ReturnType<typeof setTimeout> | null = null;
  private repairTimer: ReturnType<typeof setTimeout> | null = null;
  /** Repair runs since the last success (picks the backoff delay). */
  private repairRuns = 0;
  /** The generation whose local write failure was reported already (once per session). */
  private reportedGen: Generation | null = null;
  private booted = false;

  // ---- identity & generations -----------------------------------------------------------

  currentUser(): MeDTO | null {
    return queryClient.getQueryData<MeDTO | null>(ME_KEY) ?? null;
  }

  /** The generation of `userId`, replacing (and wiping memory of) another user's generation. */
  generationFor(userId: string): Generation {
    if (this.gen && !this.gen.dead && this.gen.userId === userId) return this.gen;
    if (this.gen) this.reset(this.gen);
    const gen = new Generation(
      userId,
      (g) => this.reset(g),
      (g, e) => this.onLocalWriteFailed(g, e),
    );
    this.gen = gen;
    this.boot();
    return gen;
  }

  /** Current generation if it belongs to `userId`. */
  generationOf(userId: string): Generation | null {
    return this.gen && !this.gen.dead && this.gen.userId === userId ? this.gen : null;
  }

  private reset(gen: Generation): void {
    gen.kill();
    if (gen !== this.gen) return;
    for (const s of this.stores.values()) s.kill();
    this.stores.clear();
    this.gen = null;
    this.req = null;
    this.clearTimers();
    this.failures = 0;
    this.phase = 'idle';
    this.genId++;
    queueMicrotask(() => {
      for (const fn of this.genListeners) fn();
    });
  }

  subscribeGen = (fn: () => void): (() => void) => {
    this.genListeners.add(fn);
    return () => this.genListeners.delete(fn);
  };

  getGenId = (): number => this.genId;

  // ---- stores ---------------------------------------------------------------------------

  storeFor(me: MeDTO, file: SongFileDTO): SheetStore {
    const gen = this.generationFor(me.id);
    let s = this.stores.get(file.id);
    if (!s || s.dead || s.gen !== gen) {
      s = new SheetStore(this, gen, me, file);
      this.stores.set(file.id, s);
    }
    return s;
  }

  /** Re-registers a disposed store of the current generation (false when it was replaced). */
  adopt(s: SheetStore): boolean {
    if (s.gen !== this.gen || s.gen.dead) return false;
    const cur = this.stores.get(s.fileId);
    if (cur && cur !== s && !cur.dead) return false;
    this.stores.set(s.fileId, s);
    return true;
  }

  dropStore(s: SheetStore): void {
    if (this.stores.get(s.fileId) === s) this.stores.delete(s.fileId);
  }

  storeOf(sheetFileId: string, gen: Generation): SheetStore | null {
    const s = this.stores.get(sheetFileId);
    return s && !s.dead && s.gen === gen ? s : null;
  }

  private hasOpenStores(): boolean {
    for (const s of this.stores.values()) if (s.isOpen) return true;
    return false;
  }

  /** Re-render every store (sync status / pending counts changed). */
  emitState(): void {
    for (const s of this.stores.values()) s.onEngineState();
  }

  // ---- triggers -------------------------------------------------------------------------

  /** Debounced flush after local edits. */
  noteEdit(): void {
    if (this.editTimer) clearTimeout(this.editTimer);
    this.editTimer = setTimeout(() => {
      this.editTimer = null;
      void this.schedule({});
    }, EDIT_FLUSH_DELAY_MS);
  }

  /** Flush now (e.g. song closed) instead of waiting for the edit debounce. */
  flushSoon(): void {
    if (this.editTimer) {
      clearTimeout(this.editTimer);
      this.editTimer = null;
    }
    void this.schedule({});
  }

  boot(): void {
    if (this.booted || typeof window === 'undefined') return;
    this.booted = true;
    window.addEventListener('online', () => {
      this.failures = 0;
      if (this.retryTimer) clearTimeout(this.retryTimer);
      this.retryTimer = null;
      void this.schedule({ pullAll: true });
    });
    window.addEventListener('offline', () => {
      if (this.phase !== 'offline') {
        this.phase = 'offline';
        this.emitState();
      }
    });
    document.addEventListener('visibilitychange', () => {
      // Visible: catch up. Hidden: push what we have before iOS suspends the app.
      void this.schedule(document.visibilityState === 'visible' ? { pullAll: true } : {});
    });
    setInterval(() => {
      if (document.visibilityState !== 'visible' || this.retryTimer) return;
      const me = this.currentUser();
      if (!me) return;
      const gen = this.generationOf(me.id);
      // No generation yet (signed in after boot, nothing opened): one cycle restores and sends the stored queue.
      if (!gen || this.hasOpenStores() || gen.outbox.countFor(me.id) > 0) void this.schedule({ pullAll: true });
    }, SYNC_INTERVAL_MS);
  }

  private clearTimers(): void {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    if (this.editTimer) clearTimeout(this.editTimer);
    if (this.repairTimer) clearTimeout(this.repairTimer);
    this.retryTimer = null;
    this.editTimer = null;
    this.repairTimer = null;
    this.repairRuns = 0;
  }

  // ---- local write failures -------------------------------------------------------------

  /**
   * A transaction of `gen` failed (it flagged itself): show it, repair later, and report it once —
   * only when a repair has failed as well. A first failure is usually WebKit dropping the connection
   * of a suspended app ("Unable to open cursor"), which the repair fixes; a failing repair means the
   * database stays unwritable.
   */
  private onLocalWriteFailed(gen: Generation, e: unknown): void {
    if (gen !== this.gen) return;
    if (this.repairRuns > 0 && this.reportedGen !== gen) {
      this.reportedGen = gen;
      reportLocalWriteFailure(e);
    }
    this.emitState();
    this.scheduleRepair(gen);
  }

  private scheduleRepair(gen: Generation): void {
    if (this.repairTimer || gen !== this.gen || gen.dead) return;
    const delay = REPAIR_DELAYS_MS[Math.min(this.repairRuns, REPAIR_DELAYS_MS.length - 1)]!;
    this.repairTimer = setTimeout(() => {
      this.repairTimer = null;
      void this.repairLocal(gen);
    }, delay);
  }

  /**
   * Writes the authoritative in-memory state again — the whole outbox and every live sheet store —
   * after a failed local write. Resolves true once IndexedDB holds it (or nothing needed repair);
   * a failing repair flags the generation again and schedules the next attempt (backoff).
   */
  async repairLocal(gen: Generation | null = this.gen): Promise<boolean> {
    if (!gen || gen !== this.gen || gen.dead || !gen.db) return false;
    if (!gen.writeFailed) return true;
    this.repairRuns++;
    // WebKit can drop the connection of a suspended app: open it again (a failure shows in run()).
    try {
      if (!gen.db.isOpen()) await gen.db.open();
    } catch {
      /* the run below fails and schedules the next attempt */
    }
    if (gen.dead || !gen.db) return false;
    const stores = [...this.stores.values()].filter((s) => s.gen === gen && !s.dead);
    const ok = await gen.run(async (db) => {
      await db.outbox.bulkPut([...gen.outbox.ordered()]);
      for (const s of stores) await s.persistAll(db);
      await db.meta.put({ k: META.lastHlc, v: gen.clock.value });
      return true;
    });
    // Runs before any later write of the chain starts: a failure of one of those flags it again.
    if (ok !== true || gen.dead || gen !== this.gen) return false;
    gen.writeFailed = false;
    this.repairRuns = 0;
    if (this.repairTimer) clearTimeout(this.repairTimer);
    this.repairTimer = null;
    this.emitState();
    return true;
  }

  // ---- sync loop ------------------------------------------------------------------------

  /** Requests a sync cycle; resolves when the loop that covers this request has finished. */
  schedule(opts: ScheduleOptions): Promise<void> {
    const r = (this.req ??= { pullSheets: new Set(), pullAll: false, retryFailed: false });
    if (opts.sheets) for (const id of opts.sheets) r.pullSheets.add(id);
    if (opts.pullAll) r.pullAll = true;
    if (opts.retryFailed) r.retryFailed = true;
    if (!this.loop) {
      this.loop = (async () => {
        try {
          while (this.req) {
            const next = this.req;
            this.req = null;
            try {
              await this.cycle(next);
            } catch (e) {
              log('sync cycle failed', e);
            }
          }
        } finally {
          this.loop = null;
          this.setRunning(false);
        }
      })();
    }
    return this.loop;
  }

  private setRunning(v: boolean): void {
    if (this.running === v) return;
    this.running = v;
    this.emitState();
  }

  private setPhase(p: SyncPhase): void {
    if (this.phase === p) return;
    this.phase = p;
    this.emitState();
  }

  private async cycle(r: SyncRequest): Promise<void> {
    const me = this.currentUser();
    if (!me) return;
    const gen = this.generationFor(me.id);
    await gen.ready;
    if (gen.dead) return;
    const userId = me.id;
    const wantsPull = r.pullAll || r.pullSheets.size > 0;
    if (!wantsPull && !r.retryFailed && gen.outbox.countFor(userId) === 0) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      this.setPhase('offline');
      return;
    }

    this.setRunning(true);
    // "Reyna aftur" (or logout) while local writes fail: try to store the edits right away too.
    if (r.retryFailed && gen.writeFailed) await this.repairLocal(gen);
    await this.absorbStored(gen);
    if (gen.dead) return;
    if (r.retryFailed) {
      const changed = gen.outbox.clearFailed(userId);
      if (changed.length) {
        void gen.run((db) => db.outbox.bulkPut(changed));
        this.emitState();
      }
    }
    const stale = new Set<string>();
    let outcome = await this.flushOutbox(gen, userId, stale);
    if (outcome === 'ok') {
      for (const store of [...this.stores.values()]) {
        if (gen.dead) break;
        if (!store.isOpen || store.gen !== gen) continue;
        if (!r.pullAll && !r.pullSheets.has(store.fileId) && !stale.has(store.fileId)) continue;
        const o = await store.pull();
        if (o === 'offline' || o === 'server' || o === 'auth') {
          outcome = o;
          break;
        }
      }
    }
    if (gen.dead) return;
    this.afterCycle(outcome);
  }

  private afterCycle(outcome: NetOutcome): void {
    if (outcome === 'ok' || outcome === 'fail') {
      this.failures = 0;
      if (this.retryTimer) clearTimeout(this.retryTimer);
      this.retryTimer = null;
      this.setPhase('idle');
      return;
    }
    this.setPhase(outcome);
    if (outcome === 'auth') return; // wait for the user to sign in again
    this.failures++;
    if (!this.retryTimer) {
      this.retryTimer = setTimeout(() => {
        this.retryTimer = null;
        void this.schedule({ pullAll: true });
      }, backoffMs(this.failures));
    }
  }

  /**
   * Adds ops that another tab stored and this one doesn't hold yet (a closed tab's edits are sent
   * by the one still open). Reads through the write chain, so this tab's own pending writes and
   * deletes have landed first; only unknown keys are fetched.
   */
  private async absorbStored(gen: Generation): Promise<void> {
    if (!gen.db) return;
    const recs = await gen.run(async (db): Promise<OpRecord[]> => {
      const keys = (await db.outbox.toCollection().primaryKeys()) as string[];
      const missing = keys.filter((k) => !gen.outbox.has(k));
      if (!missing.length) return [];
      const found: OpRecord[] = [];
      for (const r of await db.outbox.bulkGet(missing)) if (r) found.push(r);
      return found;
    });
    if (!recs?.length || gen.dead) return;
    if (gen.outbox.absorb(recs)) this.emitState();
  }

  /** Sends this user's queued ops in FIFO order. Rejected ops are marked failed; the rest continue. */
  private async flushOutbox(gen: Generation, userId: string, stale: Set<string>): Promise<NetOutcome> {
    const attempted = new Map<string, number>();
    const blocked = new Set<string>();
    for (const op of gen.outbox.ordered()) {
      if (op.userId === userId && op.failed && op.type === 'layer.upsert') blocked.add(op.layerId);
    }
    const eligible = (op: OpRecord) =>
      op.userId === userId && !op.failed && attempted.get(op.key) !== op.rev && !(op.type === 'ann' && blocked.has(op.layerId));

    for (;;) {
      if (gen.dead || this.currentUser()?.id !== userId) return 'auth';
      const step = nextStep(gen.outbox.ordered(), eligible);
      if (!step) return 'ok';
      const o =
        step.kind === 'push'
          ? await this.pushOps(gen, step.ops as AnnOp[], attempted, stale)
          : await this.sendLayerOp(gen, step.op, attempted, blocked, stale);
      if (o !== 'ok') return o;
    }
  }

  private async pushOps(gen: Generation, ops: AnnOp[], attempted: Map<string, number>, stale: Set<string>): Promise<NetOutcome> {
    for (const op of ops) attempted.set(op.key, op.rev);
    let res: PushAnnotationsResponse;
    try {
      res = await transport.push(ops.map((o) => o.payload));
    } catch (e) {
      const c = classifyError(e);
      if (c !== 'fail') return c;
      if (ops.length > 1) {
        // Isolate the rejected op(s): bisect so one bad row doesn't block the others.
        const mid = Math.ceil(ops.length / 2);
        for (const part of [ops.slice(0, mid), ops.slice(mid)]) {
          const o = await this.pushOps(gen, part, attempted, stale);
          if (o !== 'ok') return o;
        }
        return 'ok';
      }
      this.failOp(gen, ops[0]!, e);
      return 'ok';
    }
    if (gen.dead) return 'ok';
    const status = new Map<string, string>();
    for (const r of Array.isArray(res?.results) ? res.results : []) if (r && typeof r.id === 'string') status.set(r.id, r.status);
    const removed: OpRecord[] = [];
    for (const op of ops) {
      const st = status.get(op.payload.id);
      if (!st) continue; // no verdict: keep it for the next flush
      const rem = gen.outbox.remove(op.key, op.rev);
      if (!rem) continue; // edited again while in flight: the newer version is still queued
      removed.push(rem);
      if (st === 'stale') stale.add(op.sheetFileId);
      else if (st === 'forbidden') {
        this.revertForbidden(gen, op);
        stale.add(op.sheetFileId);
      }
    }
    if (removed.length) {
      void gen.run((db) => db.deleteOps(removed));
      this.emitState();
    }
    return 'ok';
  }

  private async sendLayerOp(
    gen: Generation,
    op: OpRecord,
    attempted: Map<string, number>,
    blocked: Set<string>,
    stale: Set<string>,
  ): Promise<NetOutcome> {
    attempted.set(op.key, op.rev);
    try {
      if (op.type === 'layer.upsert') {
        const dto = asLayerDTO(await transport.upsertLayer(op.payload));
        this.removeOp(gen, op);
        if (dto) this.storeOf(op.sheetFileId, gen)?.onLayerConfirmed(dto);
      } else if (op.type === 'layer.rename') {
        await transport.renameLayer(op.layerId, op.payload.name);
        this.removeOp(gen, op);
      } else if (op.type === 'layer.delete') {
        await transport.deleteLayer(op.layerId);
        this.removeOp(gen, op);
      }
      return 'ok';
    } catch (e) {
      if (e instanceof ApiFailure && (e.status === 410 || (e.status === 404 && op.type !== 'layer.upsert'))) {
        // Layer already deleted on the server: nothing left to do (the next pull removes it locally).
        this.removeOp(gen, op);
        stale.add(op.sheetFileId);
        return 'ok';
      }
      const c = classifyError(e);
      if (c !== 'fail') return c;
      this.failOp(gen, op, e);
      if (op.type === 'layer.upsert') blocked.add(op.layerId);
      return 'ok';
    }
  }

  private removeOp(gen: Generation, op: OpRecord): void {
    const rem = gen.outbox.remove(op.key, op.rev);
    if (!rem) return;
    void gen.run((db) => db.deleteOps([rem]));
    this.emitState();
  }

  private failOp(gen: Generation, op: OpRecord, e: unknown): void {
    const rec = gen.outbox.markFailed(op.key, op.rev, failureOf(e, Date.now()));
    if (!rec) return;
    log(`op rejected (${rec.failed?.status} ${rec.failed?.code})`, op.key);
    void gen.run((db) => db.outbox.put(rec));
    this.emitState();
  }

  /**
   * The server refused an edit: drop the optimistic local version and forget the layer's cursor,
   * so the next pull delivers the layer in full (server truth).
   */
  private revertForbidden(gen: Generation, op: AnnOp): void {
    const store = this.storeOf(op.sheetFileId, gen);
    if (store) {
      store.revertObject(op.payload.id, op.layerId);
      return;
    }
    if (gen.outbox.has(annKey(op.payload.id))) return; // edited again since: that op decides
    void gen.run(async (db) => {
      await db.objects.delete(op.payload.id);
      await db.cursors.delete(op.layerId);
    });
  }

  // ---- public helpers (logout / offline team) -----------------------------------------

  async pendingCount(userId: string): Promise<number> {
    const gen = this.generationOf(userId);
    if (gen) {
      await gen.ready;
      if (!gen.dead) return gen.outbox.countFor(userId);
    }
    return peekPendingOps(userId);
  }

  /** Flushes the signed-in user's queue (failed ops get one more try). Never rejects. */
  async flushAll(): Promise<void> {
    if (!this.currentUser()) return;
    if (this.editTimer) {
      clearTimeout(this.editTimer);
      this.editTimer = null;
    }
    try {
      await this.schedule({ retryFailed: true });
    } catch {
      /* never */
    }
  }

  /**
   * Removes everything stored locally for these sheets (no longer visible / deleted). Resolves true
   * when the device holds nothing of them any more, false when the stored copies could not be
   * removed (the caller keeps them listed and tries again).
   */
  async purgeSheets(sheetFileIds: readonly string[]): Promise<boolean> {
    const ids = new Set(sheetFileIds);
    if (!ids.size) return true;
    const me = this.currentUser();
    const gen = me ? this.generationFor(me.id) : this.gen;
    if (!gen) return purgeWithoutGeneration(ids);
    await gen.ready;
    if (gen.dead) return false;
    for (const id of ids) this.storeOf(id, gen)?.purgeLocal();
    const dropped = gen.outbox.removeWhere((op) => ids.has(op.sheetFileId));
    if (dropped.length) this.emitState();
    // Memory-only session (IndexedDB could not be opened): whatever is stored is reached directly.
    if (!gen.db) return purgeWithoutGeneration(ids);
    const done = await gen.run(async (db) => {
      await purgeTables(db, ids);
      await db.deleteOps(dropped);
      return true;
    });
    return done === true;
  }

  /**
   * Reconciliation with the authoritative manifest of `userId`: removes the local layers, objects
   * and queued changes of every sheet that is not in `visibleSheetIds` — also sheets no manifest
   * diff ever listed (opened between two syncs, or a purge that was interrupted). Never touches a
   * sheet with a live store (opened after the manifest was fetched) or with changes still waiting
   * to be sent (the server answers those itself; a refused one leaves the queue and the next sync
   * removes the sheet). Resolves false when nothing could be checked.
   */
  async purgeSheetsNotIn(userId: string, visibleSheetIds: Iterable<string>): Promise<boolean> {
    const me = this.currentUser();
    if (!me || me.id !== userId) return false;
    const visible = new Set(visibleSheetIds);
    const gen = this.generationFor(me.id);
    await gen.ready;
    if (gen.dead) return false;
    const stored = gen.db
      ? await gen.run(async (db) => {
          const ids = new Set((await db.layers.orderBy('sheetFileId').uniqueKeys()) as string[]);
          for (const k of (await db.outbox.orderBy('sheetFileId').uniqueKeys()) as string[]) ids.add(k);
          return [...ids];
        })
      : [];
    if (!stored || gen.dead) return false;
    const waiting = new Set<string>();
    for (const op of gen.outbox.ordered()) if (!op.failed) waiting.add(op.sheetFileId);
    const known = new Set([...stored, ...gen.outbox.ordered().map((op) => op.sheetFileId)]);
    const gone = [...known].filter((id) => !visible.has(id) && !this.storeOf(id, gen) && !waiting.has(id));
    return gone.length === 0 ? true : this.purgeSheets(gone);
  }

  /**
   * The session ended without a logout (401: deactivated, revoked, signed out everywhere, password
   * reset): remove what was pulled from the server — every layer, object and cursor — but keep this
   * device's unsent changes (the outbox) and the own layers they belong to, so the same user signing
   * in again sends them (a different user wipes the whole database instead: offline/wipe.ts).
   * Memory goes too (the generation is ended) — unless the unsent changes could not be stored, then
   * memory keeps the only copy of them (nothing is shown while signed out). Never rejects.
   */
  async forgetPulled(): Promise<void> {
    const gen = this.gen;
    if (gen && !gen.dead) {
      await gen.ready;
      // Edits still in the write chain land first; the in-memory outbox is stored once more.
      const stored = gen.db
        ? await gen.run(async (db) => {
            await db.outbox.bulkPut([...gen.outbox.ordered()]);
            return true;
          })
        : undefined;
      if (stored === true || gen.outbox.size === 0) this.reset(gen);
    }
    try {
      await withExistingDb((db) => db.transaction('rw', db.tables, () => keepOnlyPending(db)), undefined);
    } catch (e) {
      console.warn('[annotations] pulled copies could not be removed', e);
    }
  }
}

/** Deletes everything but the outbox, the own layers it refers to (with their objects) and meta. */
async function keepOnlyPending(db: AnnotationDb): Promise<void> {
  const keep = [...new Set((await db.outbox.toArray()).map((op) => op.layerId))];
  await db.objects.where('layerId').noneOf(keep).delete();
  await db.layers.where('id').noneOf(keep).delete();
  await db.prefs.where('layerId').noneOf(keep).delete();
  await db.cursors.where('layerId').noneOf(keep).delete();
}

async function purgeTables(db: AnnotationDb, ids: ReadonlySet<string>): Promise<void> {
  const list = [...ids];
  const layerIds = (await db.layers.where('sheetFileId').anyOf(list).primaryKeys()) as string[];
  if (layerIds.length) {
    await db.objects.where('layerId').anyOf(layerIds).delete();
    await db.layers.bulkDelete(layerIds);
    await db.cursors.bulkDelete(layerIds);
    await db.prefs.bulkDelete(layerIds);
  }
  await db.outbox.where('sheetFileId').anyOf(list).delete();
  await db.meta.bulkDelete(list.flatMap((id) => [META.active(id), META.hideStale(id)]));
}

/** Runs `fn` on a short-lived connection; `absent` when there is no database (rejects on failure). */
async function withExistingDb<T>(fn: (db: AnnotationDb) => Promise<T>, absent: T): Promise<T> {
  if (typeof indexedDB === 'undefined') return absent;
  if (!(await Dexie.exists(ANNOTATION_DB_NAME))) return absent;
  const db = new AnnotationDb();
  try {
    await db.open();
    return await fn(db);
  } finally {
    db.close({ disableAutoOpen: true });
  }
}

function peekPendingOps(userId: string): Promise<number> {
  return withExistingDb((db) => db.outbox.where('userId').equals(userId).count(), 0).catch(() => 0);
}

function purgeWithoutGeneration(ids: ReadonlySet<string>): Promise<boolean> {
  return withExistingDb((db) => db.transaction('rw', db.tables, () => purgeTables(db, ids)).then(() => true), true).catch(() => false);
}

export const engine = new AnnotationEngine();
